"use client";

import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ExternalLink } from "lucide-react";

interface InfoBlock {
  title: string;
  content: string;
}

interface ProjectHeroProps {
  title: string;
  subtitle?: string;
  category: string;
  year: string;
  featured?: boolean;
  link?: string;
  infoBlocks: InfoBlock[];
  backgroundImage?: string;
}

const ProjectHero: React.FC<ProjectHeroProps> = ({
  title,
  subtitle,
  link,
  infoBlocks,
  backgroundImage,
}) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hero-title, .hero-subtitle, .hero-meta, .info-blocks, .hero-image",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          stagger: 0.1,
          ease: "power2.out",
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      className="bg-[#fafcfd] pt-24 pb-10 sm:pt-32 sm:pb-16 md:pt-32 md:pb-20 lg:pt-36"
    >
      <div className="container mx-auto px-3 xs:px-4">
        <div className="max-w-7xl mx-auto pt-6 sm:pt-16 md:pt-24">
          {/* Project Meta */}
          <div className="hero-meta flex flex-wrap items-center gap-6 mb-8 opacity-0"></div>

          {/* Project Title */}
          <div ref={titleRef}>
            <h1 className="hero-title text-xl xs:text-3xl sm:text-3xl md:text-8xl lg:text-7xl font-bold mb-4 xs:mb-6 sm:mb-8 leading-[1.1] xs:leading-[1.0] sm:leading-[0.9] tracking-tight opacity-0 font-pp-acma text-[#111111]">
              {title}
            </h1>
          </div>

          {/* Subtitle */}
          {subtitle && (
            <p className="hero-subtitle text-base xs:text-md sm:text-xl md:text-2xl text-[#6b6b6b] leading-relaxed max-w-4xl mb-8 xs:mb-10 sm:mb-12 md:mb-14 opacity-0 font-neue-regrade-variable">
              {subtitle}
            </p>
          )}

          {/* Info Blocks */}
          <div className="info-blocks opacity-0 mb-8 xs:mb-12 sm:mb-16 relative z-10">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-6xl">
              {infoBlocks.map((block, index) => (
                <div key={index} className="space-y-1 xs:space-y-2">
                  <h3 className="text-sm xs:text-base sm:text-lg text-[#6f6f6f] uppercase tracking-wider font-pp-neuebit">
                    {block.title}
                  </h3>
                  <p className="text-base xs:text-base sm:text-lg md:text-xl font-semibold text-[#111111] break-words font-neue-regrade-variable mb-3">
                    {block.content}
                  </p>
                  {/* Add button to Platform column */}
                  {block.title.toLowerCase() === "platform" && link && (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 font-bold text-[20px] font-pp-neuebit cursor-pointer relative z-20 shadow-lg hover:shadow-xl"
                      style={{
                        paddingTop: "4px",
                        paddingBottom: "4px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        borderRadius: "8px",
                      }}
                    >
                      App Store
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image Section */}
          {backgroundImage && (
            <div className="hero-image opacity-0 -mx-3 xs:-mx-4">
              <div className="w-full h-[200px] xs:h-[250px] sm:h-[300px] md:h-[400px] overflow-hidden">
                <img
                  src={backgroundImage}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProjectHero;
